import { useEffect, useId, useRef, type ReactNode } from "react";
import { IconChevronDown } from "./icons";

/**
 * Knop met een klein paneel eronder (Weergave, ⋯). Escape en klikken buiten het paneel sluiten het;
 * na Escape gaat de focus terug naar de knop. `open` staat bij de aanroeper, zodat een actie het paneel kan sluiten.
 */
export function ToolPopover({
  label,
  title,
  icon,
  hideLabel = false,
  open,
  onOpenChange,
  children,
}: {
  /** Tekst op de knop (of alleen de toegankelijke naam bij `hideLabel`). */
  label: string;
  /** Naam van het paneel. */
  title: string;
  icon: ReactNode;
  hideLabel?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>("button, select, input")?.focus();
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onOpenChange(false);
        btn.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div className="toolpop" ref={ref}>
      <button
        ref={btn}
        type="button"
        className={`btn small ${hideLabel ? "ico" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={hideLabel ? label : undefined}
        title={hideLabel ? label : undefined}
        onClick={() => onOpenChange(!open)}
      >
        {icon}
        {!hideLabel && <span className="toollabel">{label}</span>}
        {!hideLabel && <IconChevronDown size={16} aria-hidden="true" className="toollabel" />}
      </button>
      {open && (
        <div ref={panel} className="popover toolpanel" id={id} role="dialog" aria-label={title}>
          {children}
        </div>
      )}
    </div>
  );
}
